import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Search, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { partnershipsApiService } from '../../services/partnershipsApi';
import {
  PartnershipCreator,
  PartnershipModpack,
  PartnershipTag,
  PARTNERSHIP_STATUSES,
  PLATFORM_TYPES,
  PlatformType,
  CreatorType,
  PartnershipStatus,
} from '../../types/partnership';
import { cn } from '../../utils/cn';

interface CreatePartnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-fill from modpack browser context — pack name to auto-resolve */
  prefillPackName?: string;
  /** Pre-fill from modpack browser context — pack summary for partnership summary */
  prefillPackSummary?: string;
  /** Pre-fill from modpack browser context — thumbnail URL to use as modpack logo */
  prefillPackThumbnailUrl?: string;
  /** Pre-fill from modpack browser context — platform listing URL (e.g. CurseForge/Modrinth page) */
  prefillPlatformUrl?: string;
  /** Suggested creator names from Apogee pack enrichment (Modrinth/CurseForge authors) */
  prefillAuthorCandidates?: string[];
}

const STEPS = [
  { id: 1, label: 'Creator' },
  { id: 2, label: 'Modpack' },
  { id: 3, label: 'Details' },
];

export const CreatePartnershipModal = ({
  isOpen,
  onClose,
  onSuccess,
  prefillPackName,
  prefillPackSummary,
  prefillPackThumbnailUrl,
  prefillPlatformUrl,
  prefillAuthorCandidates,
}: CreatePartnershipModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [step, setStep] = useState(1);

  // Step 1: Creator
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState<PartnershipCreator[]>([]);
  const [selectedCreator, setSelectedCreator] = useState<PartnershipCreator | null>(null);
  const [isSearchingCreators, setIsSearchingCreators] = useState(false);
  const [showCreateCreator, setShowCreateCreator] = useState(false);
  const [newCreatorName, setNewCreatorName] = useState('');
  const [newCreatorType, setNewCreatorType] = useState<CreatorType>('individual');
  const [isCreatingCreator, setIsCreatingCreator] = useState(false);

  // Step 2: Modpack
  const [modpacks, setModpacks] = useState<PartnershipModpack[]>([]);
  const [isLoadingModpacks, setIsLoadingModpacks] = useState(false);
  const [selectedModpack, setSelectedModpack] = useState<PartnershipModpack | null>(null);
  const [showCreateModpack, setShowCreateModpack] = useState(false);
  const [newModpackName, setNewModpackName] = useState('');
  const [newModpackPlatformType, setNewModpackPlatformType] = useState<PlatformType | ''>('');
  const [newModpackPlatformUrl, setNewModpackPlatformUrl] = useState('');
  const [isCreatingModpack, setIsCreatingModpack] = useState(false);

  // Step 3: Partnership details
  const [status, setStatus] = useState<PartnershipStatus>('prospecting');
  const [priority, setPriority] = useState(5);
  const [summary, setSummary] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [tags, setTags] = useState<PartnershipTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [contactDate, setContactDate] = useState('');
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrefillResolving, setIsPrefillResolving] = useState(false);

  const debouncedCreatorSearch = useDebounce(creatorSearch, 300);

  // Search creators
  useEffect(() => {
    if (!debouncedCreatorSearch || debouncedCreatorSearch.length < 1) {
      setCreatorResults([]);
      return;
    }
    setIsSearchingCreators(true);
    partnershipsApiService
      .searchCreators(debouncedCreatorSearch)
      .then(setCreatorResults)
      .catch(console.error)
      .finally(() => setIsSearchingCreators(false));
  }, [debouncedCreatorSearch]);

  // Load modpacks when creator is selected
  useEffect(() => {
    if (!selectedCreator?.id) {
      setModpacks([]);
      setSelectedModpack(null);
      setShowCreateModpack(false);
      return;
    }
    setIsLoadingModpacks(true);
    partnershipsApiService
      .getModpacksByCreator(selectedCreator.id)
      .then(setModpacks)
      .catch(console.error)
      .finally(() => setIsLoadingModpacks(false));
  }, [selectedCreator?.id]);

  // Load tags for step 3
  useEffect(() => {
    if (step !== 3) return;
    setIsLoadingTags(true);
    partnershipsApiService
      .getAllTags()
      .then(setTags)
      .catch(console.error)
      .finally(() => setIsLoadingTags(false));
  }, [step]);

  // Detect platform type from a URL string
  const detectPlatformType = (url: string): PlatformType | '' => {
    if (/curseforge\.com/i.test(url)) return 'curseforge';
    if (/modrinth\.com/i.test(url)) return 'modrinth';
    return '';
  };

  // Apply prefilled platform URL + auto-detected type to "create new modpack" fields
  const applyPrefillPlatformUrl = () => {
    if (prefillPlatformUrl) {
      setNewModpackPlatformUrl(prefillPlatformUrl);
      const detected = detectPlatformType(prefillPlatformUrl);
      if (detected) setNewModpackPlatformType(detected);
    }
  };

  // Auto-resolve from prefill pack name (from modpack browser context)
  useEffect(() => {
    if (!prefillPackName || !isOpen) return;

    let cancelled = false;
    setIsPrefillResolving(true);

    const resolve = async () => {
      try {
        // Search for matching modpacks in the partnership system
        const results = await partnershipsApiService.searchModpacks(prefillPackName);
        if (cancelled) return;

        // Find the best match — prefer exact name match, fall back to first result
        const normalizedName = prefillPackName.toLowerCase().trim();
        const exactMatch = results.find(
          (m) => m.name.toLowerCase().trim() === normalizedName
        );
        const match = exactMatch || (results.length > 0 ? results[0] : null);

        if (match) {
          // Load the creator for this modpack
          const creator = await partnershipsApiService.getCreatorById(match.creator_id);
          if (cancelled) return;

          // Auto-select creator + modpack and skip to Step 3
          setSelectedCreator(creator);
          setSelectedModpack(match);
          setModpacks([match]);
          setStep(3);
        } else {
          // No matching modpack found — prefill the "create new modpack" name
          setNewModpackName(prefillPackName);
          applyPrefillPlatformUrl();

          // Resolve creator from enrichment author names when possible
          const candidates = (prefillAuthorCandidates || []).map((c) => c.trim()).filter(Boolean);
          let linkedCreator: PartnershipCreator | null = null;
          for (const candidate of candidates) {
            try {
              const creators = await partnershipsApiService.searchCreators(candidate);
              if (cancelled) return;
              const exact = creators.find(
                (c) => c.name.toLowerCase().trim() === candidate.toLowerCase(),
              );
              if (exact) {
                linkedCreator = exact;
                break;
              }
            } catch {
              // continue with next candidate
            }
          }
          if (linkedCreator) {
            setSelectedCreator(linkedCreator);
            setShowCreateCreator(false);
            setNewCreatorName('');
            setStep(2);
          } else if (candidates.length > 0) {
            setNewCreatorName(candidates[0]);
            setShowCreateCreator(true);
            setStep(1);
          }
        }

        // Prefill summary with pack context
        if (prefillPackSummary) {
          setSummary(prefillPackSummary);
        } else {
          setSummary(`Partnership prospect for ${prefillPackName}`);
        }
      } catch {
        // Silently fail — prefill the modpack name so user can still proceed manually
        setNewModpackName(prefillPackName);
        applyPrefillPlatformUrl();
        setSummary(`Partnership prospect for ${prefillPackName}`);
        const candidates = (prefillAuthorCandidates || []).map((c) => c.trim()).filter(Boolean);
        if (candidates.length > 0) {
          setNewCreatorName(candidates[0]);
          setShowCreateCreator(true);
          setStep(1);
        }
      } finally {
        if (!cancelled) setIsPrefillResolving(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [prefillPackName, prefillPackSummary, prefillPlatformUrl, prefillAuthorCandidates, isOpen]);

  if (!isOpen) return null;

  const handleSelectCreator = (creator: PartnershipCreator) => {
    setSelectedCreator(creator);
    setCreatorSearch('');
    setCreatorResults([]);
    setShowCreateCreator(false);
    setSelectedModpack(null);
  };

  const handleRemoveCreator = () => {
    setSelectedCreator(null);
    setSelectedModpack(null);
    setModpacks([]);
    setShowCreateModpack(false);
  };

  const handleCreateCreator = async () => {
    if (!newCreatorName.trim()) {
      setError('Creator name is required');
      return;
    }
    setError(null);
    setIsCreatingCreator(true);
    try {
      const result = await partnershipsApiService.createCreator({
        name: newCreatorName.trim(),
        type: newCreatorType,
      });
      const newCreator: PartnershipCreator = {
        id: result.id,
        name: newCreatorName.trim(),
        type: newCreatorType,
        contact_info: null,
        platform_links: null,
        website: null,
        notes: null,
        created_at: Date.now() / 1000,
        created_by: '',
        updated_at: null,
        updated_by: null,
      };
      setSelectedCreator(newCreator);
      setNewCreatorName('');
      setNewCreatorType('individual');
      setShowCreateCreator(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create creator');
    } finally {
      setIsCreatingCreator(false);
    }
  };

  const handleSelectModpack = (modpack: PartnershipModpack) => {
    setSelectedModpack(modpack);
    setShowCreateModpack(false);
  };

  const handleRemoveModpack = () => {
    setSelectedModpack(null);
  };

  const handleCreateModpack = async () => {
    if (!selectedCreator?.id || !newModpackName.trim()) {
      setError('Modpack name is required');
      return;
    }
    setError(null);
    setIsCreatingModpack(true);
    try {
      const logoUrl = prefillPackThumbnailUrl?.trim() || undefined;
      const result = await partnershipsApiService.createModpack({
        creator_id: selectedCreator.id,
        name: newModpackName.trim(),
        platform_type: newModpackPlatformType || undefined,
        platform_url: newModpackPlatformUrl.trim() || undefined,
        logo_url: logoUrl,
      });
      const newModpack: PartnershipModpack = {
        id: result.id,
        creator_id: selectedCreator.id,
        name: newModpackName.trim(),
        description: null,
        platform_url: newModpackPlatformUrl.trim() || null,
        platform_slug: null,
        platform_type: newModpackPlatformType || null,
        logo_url: logoUrl || null,
        player_count_estimate: null,
        created_at: Date.now() / 1000,
        created_by: '',
        updated_at: null,
        updated_by: null,
      };
      setSelectedModpack(newModpack);
      setModpacks((prev) => [...prev, newModpack]);
      setNewModpackName('');
      setNewModpackPlatformType('');
      setNewModpackPlatformUrl('');
      setShowCreateModpack(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create modpack');
    } finally {
      setIsCreatingModpack(false);
    }
  };

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedCreator?.id) {
      setError('Please select or create a creator');
      return;
    }

    setIsSubmitting(true);
    try {
      await partnershipsApiService.createPartnership({
        creator_id: selectedCreator.id,
        modpack_id: selectedModpack?.id || undefined,
        status,
        priority,
        summary: summary.trim() || undefined,
        assigned_to: assignedTo.trim() || null,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        contact_date: contactDate
          ? Math.floor(new Date(contactDate).getTime() / 1000)
          : undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create partnership');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = !!selectedCreator;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-1"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4">New Partnership</h2>

        {/* Pack context banner when opened from modpack browser */}
        {prefillPackName && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-glass-200/10 border border-glass-200/20 mb-4">
            {prefillPackThumbnailUrl && (
              <img
                src={prefillPackThumbnailUrl}
                alt={prefillPackName}
                className="w-10 h-10 rounded-lg object-cover bg-dark-900/50 flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{prefillPackName}</p>
              <p className="text-xs text-glass-400">Creating partnership for this modpack</p>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                  step === s.id && 'bg-accent-500/30 text-accent-300',
                  step > s.id && 'bg-accent-500/15 text-accent-400',
                  step < s.id && 'bg-glass-200/20 text-glass-400'
                )}
              >
                {step > s.id ? <Check className="w-4 h-4" /> : s.id}
              </div>
              <span
                className={cn(
                  'ml-1.5 text-xs hidden sm:inline',
                  step >= s.id ? 'text-white' : 'text-glass-400'
                )}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 text-glass-500 mx-1" />
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {isPrefillResolving && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-accent-500/10 border border-accent-500/20 mb-4">
            <Loader className="w-4 h-4 animate-spin text-accent-400 flex-shrink-0" />
            <span className="text-sm text-accent-300">
              Looking up &ldquo;{prefillPackName}&rdquo; in partnerships&hellip;
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Step 1: Creator */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Select Creator *</label>
                {selectedCreator ? (
                  <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                    <span className="text-sm font-medium text-white truncate">
                      {selectedCreator.name}
                      <span className="text-xs text-glass-400 ml-2">({selectedCreator.type})</span>
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveCreator}
                      className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                      aria-label="Remove creator"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                      <input
                        type="text"
                        value={creatorSearch}
                        onChange={(e) => setCreatorSearch(e.target.value)}
                        className="glass-input w-full pl-10 text-sm"
                        placeholder="Search creators..."
                      />
                      {(creatorResults.length > 0 || isSearchingCreators) && (
                        <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                          {isSearchingCreators && (
                            <div className="p-3 flex items-center justify-center">
                              <Loader className="w-4 h-4 animate-spin text-accent-400" />
                            </div>
                          )}
                          {creatorResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => handleSelectCreator(c)}
                              className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                            >
                              {c.name}
                              <span className="text-xs text-glass-400 ml-2">({c.type})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCreateCreator(!showCreateCreator)}
                      className="text-sm text-accent-400 hover:text-accent-300 mt-2"
                    >
                      Or create new
                    </button>
                    {showCreateCreator && prefillAuthorCandidates && prefillAuthorCandidates.length > 0 && (
                      <p className="text-xs text-glass-500 mt-2">
                        Suggested from pack enrichment — adjust the name if needed, then create.
                      </p>
                    )}
                    {showCreateCreator && (
                      <div className="mt-3 p-3 rounded-lg bg-glass-200/10 border border-glass-200/20 space-y-3">
                        <input
                          type="text"
                          value={newCreatorName}
                          onChange={(e) => setNewCreatorName(e.target.value)}
                          className="glass-input w-full text-sm"
                          placeholder="Creator name"
                        />
                        <select
                          value={newCreatorType}
                          onChange={(e) => setNewCreatorType(e.target.value as CreatorType)}
                          className="glass-input w-full text-sm"
                        >
                          <option value="individual">Individual</option>
                          <option value="team">Team</option>
                        </select>
                        <button
                          type="button"
                          onClick={handleCreateCreator}
                          className="glass-button w-full text-sm"
                          disabled={isCreatingCreator || !newCreatorName.trim()}
                        >
                          {isCreatingCreator ? (
                            <Loader className="w-4 h-4 animate-spin mx-auto" />
                          ) : (
                            'Create Creator'
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Modpack (optional) */}
          {step === 2 && selectedCreator && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Select Modpack (optional)</label>
                {selectedModpack ? (
                  <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                    <span className="text-sm font-medium text-white truncate">
                      {selectedModpack.name}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveModpack}
                      className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                      aria-label="Remove modpack"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    {isLoadingModpacks ? (
                      <div className="p-4 flex items-center justify-center">
                        <Loader className="w-6 h-6 animate-spin text-accent-400" />
                      </div>
                    ) : modpacks.length > 0 ? (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {modpacks.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => handleSelectModpack(m)}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-glass-200/10 text-sm text-white"
                          >
                            {m.name}
                            {m.platform_type && (
                              <span className="text-xs text-glass-400 ml-2">
                                ({m.platform_type})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-glass-400 py-2">No modpacks found for this creator.</p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowCreateModpack(!showCreateModpack)}
                      className="text-sm text-accent-400 hover:text-accent-300 mt-2"
                    >
                      Or create new
                    </button>
                    {showCreateModpack && (
                      <div className="mt-3 p-3 rounded-lg bg-glass-200/10 border border-glass-200/20 space-y-3">
                        <input
                          type="text"
                          value={newModpackName}
                          onChange={(e) => setNewModpackName(e.target.value)}
                          className="glass-input w-full text-sm"
                          placeholder="Modpack name"
                        />
                        <select
                          value={newModpackPlatformType}
                          onChange={(e) =>
                            setNewModpackPlatformType(e.target.value as PlatformType | '')
                          }
                          className="glass-input w-full text-sm"
                        >
                          <option value="">Platform (optional)</option>
                          {PLATFORM_TYPES.map((p) => (
                            <option key={p} value={p}>
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </option>
                          ))}
                        </select>
                        <input
                          type="url"
                          value={newModpackPlatformUrl}
                          onChange={(e) => setNewModpackPlatformUrl(e.target.value)}
                          className="glass-input w-full text-sm"
                          placeholder="Platform URL (optional)"
                        />
                        <button
                          type="button"
                          onClick={handleCreateModpack}
                          className="glass-button w-full text-sm"
                          disabled={isCreatingModpack || !newModpackName.trim()}
                        >
                          {isCreatingModpack ? (
                            <Loader className="w-4 h-4 animate-spin mx-auto" />
                          ) : (
                            'Create Modpack'
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Partnership Details */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PartnershipStatus)}
                  className="glass-input w-full text-sm"
                >
                  {PARTNERSHIP_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-glass-300 mb-1 block">Priority (1–10)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
                  />
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={priority}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v >= 1 && v <= 10) setPriority(v);
                    }}
                    className="glass-input w-14 text-sm text-center"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-glass-300 mb-1 block">Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="Brief partnership summary..."
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm text-glass-300 mb-1 block">Assigned to (UUID)</label>
                <input
                  type="text"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="Optional user UUID"
                />
              </div>

              <div>
                <label className="text-sm text-glass-300 mb-1 block">Tags</label>
                {isLoadingTags ? (
                  <div className="p-3 flex items-center justify-center">
                    <Loader className="w-4 h-4 animate-spin text-accent-400" />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleToggleTag(tag.id)}
                        className={cn(
                          'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                          selectedTagIds.includes(tag.id)
                            ? 'bg-accent-500/30 text-accent-300 border border-accent-500/40'
                            : 'glass-button-secondary text-sm'
                        )}
                        style={
                          selectedTagIds.includes(tag.id)
                            ? { borderColor: tag.color, backgroundColor: `${tag.color}20` }
                            : undefined
                        }
                      >
                        {tag.name}
                      </button>
                    ))}
                    {tags.length === 0 && !isLoadingTags && (
                      <span className="text-xs text-glass-400">No tags available</span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-glass-300 mb-1 block">Contact date</label>
                <input
                  type="datetime-local"
                  value={contactDate}
                  onChange={(e) => setContactDate(e.target.value)}
                  className="glass-input w-full text-sm"
                />
              </div>
            </div>
          )}

          {/* Navigation & Actions */}
          <div className="flex justify-between gap-2 pt-4 border-t border-glass-200/20">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="glass-button-secondary flex items-center gap-1"
                  disabled={isSubmitting}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              {step === 1 && (
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="glass-button flex items-center gap-1"
                  disabled={!canProceedStep1}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              {step === 2 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedModpack(null);
                      setStep(3);
                    }}
                    className="glass-button-secondary text-sm"
                  >
                    Skip — no modpack
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="glass-button flex items-center gap-1"
                    disabled={!selectedModpack}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {step === 3 && (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="glass-button-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="glass-button"
                    disabled={isSubmitting || !selectedCreator}
                  >
                    {isSubmitting ? (
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Create Partnership
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
